// Sidebar2.js
import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import { deleteArticleVersion, listArticleVersions } from "../../lib/articles";
import {
  listComments,
  resolveComment,
  deleteComment,
  listReplies,
  addReply,
  deleteReply,
} from "../../lib/comments";

const Wrapper = styled.div`
  flex: 1 1 25%;
  max-width: 25%;
  min-width: 260px;
  border-left: 1px solid #e5e7eb;
  padding: 16px;
  box-sizing: border-box;
  overflow-y: auto;
  background-color: #f8f9fa;
`;

const Section = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 12px;
  background: #fff;
`;

const SectionHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 10px 12px;
  background: white;
  border: none;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  font-weight: 600;
  align-items: center;
  &:hover {
    background: white;
  }
`;

const Score = styled.span`
  background: #e0f0ee;
  color: #00876e;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
`;

const Chevron = styled.span`
  margin-left: 8px;
  transform: ${({ open }) => (open ? "rotate(90deg)" : "rotate(0deg)")};
  transition: transform 0.15s ease;
`;

const SectionBody = styled.div`
  padding: 10px 12px 12px 12px;
`;

const Empty = styled.div`
  color: #6b7280;
  font-size: 14px;
  padding: 6px 0;
`;

const Item = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
  background: #fff;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  ${({ sev }) => {
    if (sev === "high") return "background:#fee2e2;color:#991b1b;";
    if (sev === "medium") return "background:#ffedd5;color:#9a3412;";
    return "background:#dcfce7;color:#14532d;";
  }}
`;

const Label = styled.span`
  color: #6b7280;
  font-size: 12px;
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
`;

const Reason = styled.div`
  font-size: 14px;
  color: #111827;
  line-height: 1.35;
  margin-bottom: 8px;
`;

const ShowBtn = styled.button`
  padding: 6px 10px;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    background: #1d4ed8;
  }
`;

const SmallBtn = styled.button`
  padding: 6px 10px;
  background: #374151;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    background: #111827;
  }
`;

const TopBar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  background-color: white;
  margin: -16px;
  padding: 16px;
`;

const Select = styled.select`
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const Title = styled.h3`
  margin: 30px -16px 15px -16px;
  font-size: 16px;
  font-weight: bold;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
`;

/* Comments - top toolbar button */
const AddCommentBtn = styled.button`
  background: #e9ecef;
  color: black;
  padding: 6px 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
`;

const TrashBtn = styled.button`
  background: transparent;
  border: 0;
  color: #ef4444;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1;

  &:hover {
    background: #fee2e2;
  }
`;

const categoriesMeta = [
  { key: "overall", label: "Overall" },
  { key: "readability", label: "Readability" },
  { key: "topicAuthority", label: "Topic Authority" },
  { key: "contentValue", label: "Content Value" },
  { key: "seo", label: "SEO" },
];

const markupAiChecksMeta = [
  { key: "brand_voice", label: "Brand Voice" },
  { key: "persona", label: "Persona" },
  { key: "terminology", label: "Brand Terms" },
  { key: "generic_claims", label: "Claims" },
  { key: "source_authority", label: "Source Authority" },
  { key: "freshness", label: "Freshness" },
  { key: "focus_agent", label: "Fluff" },
  { key: "answer_authority", label: "Q&A Optimization" },
  { key: "sentence_clarity", label: "Sentence Clarity" },
  { key: "ai_voice_detector", label: "AI Voice" },
  { key: "snippet_readiness", label: "Snippet Readiness" },
  { key: "key_takeaways", label: "Key Takeaways" },
];

const Sidebar2 = ({
  feedback = {},
  onShowIssue,
  currentArticleId,
  onOpenVersion,
  currentUserRole,
  currentBrief, // ✅ Received from parent (TextBox or Page)
  onEditBrief, // ✅ Function to reopen modal with brief data
  organization,
}) => {
  const userRole = currentUserRole;
  const [open, setOpen] = useState(() =>
    Object.fromEntries(categoriesMeta.map((c) => [c.key, false]))
  );
  const [mode, setMode] = useState(
    organization?.ai_enabled ? "scores" : "comments"
  );
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [internalBrief, setInternalBrief] = useState(currentBrief); // ✅ Local state

  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  // COMMENTS state
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const canAddComments = ["Admin", "Editor"].includes(userRole);
  const canResolveComments = ["Admin", "Editor", "Writer"].includes(userRole);
  const [repliesByComment, setRepliesByComment] = useState({});
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyCounts, setReplyCounts] = useState({});
  const [showResolved, setShowResolved] = useState(false);
  const [resolvedComments, setResolvedComments] = useState([]);
  const [loadingResolved, setLoadingResolved] = useState(false);

  const currentUserId = ""; // Need this if you check replies ownership, currently placeholder or from context

  useEffect(() => {
    if (organization && !organization.ai_enabled) {
      if (mode === "scores" || mode === "brief" || mode === "markupai") {
        setMode("comments");
      }
    }
  }, [organization, mode]);

  // MARKUP AI state
  const [markupChecks, setMarkupChecks] = useState({}); // { [agentKey]: { issues } | { error } }
  const [checkingMarkup, setCheckingMarkup] = useState(false);
  const [markupError, setMarkupError] = useState(null);
  const [hasCheckedMarkup, setHasCheckedMarkup] = useState(false);
  const [openMarkup, setOpenMarkup] = useState(() =>
    Object.fromEntries(markupAiChecksMeta.map((c) => [c.key, false]))
  );
  const toggleMarkup = (k) => setOpenMarkup((s) => ({ ...s, [k]: !s[k] }));

  useEffect(() => {
    function onMarkupResult(e) {
      setCheckingMarkup(false);
      setHasCheckedMarkup(true);
      setMarkupError(e.detail?.error || null);
      setMarkupChecks(e.detail?.checks || {});
    }
    window.addEventListener("markupai:result", onMarkupResult);
    return () => window.removeEventListener("markupai:result", onMarkupResult);
  }, []);

  // reset when switching articles so stale results aren't shown
  useEffect(() => {
    setMarkupChecks({});
    setMarkupError(null);
    setHasCheckedMarkup(false);
  }, [currentArticleId]);

  // remove an issue from its check's list once its suggested change is applied in the editor
  useEffect(() => {
    function onMarkupIssueApplied(e) {
      const { key, index } = e.detail || {};
      if (key == null || index == null) return;
      setMarkupChecks((prev) => {
        const check = prev[key];
        if (!check || !Array.isArray(check.issues)) return prev;
        return {
          ...prev,
          [key]: {
            ...check,
            issues: check.issues.filter((_, i) => i !== index),
          },
        };
      });
    }
    window.addEventListener("markupai:issueApplied", onMarkupIssueApplied);
    return () =>
      window.removeEventListener("markupai:issueApplied", onMarkupIssueApplied);
  }, []);

  const improvements = useMemo(() => {
    const raw = feedback.improvements || feedback?.Improvements || {};
    return {
      overall: raw["Overall"] || raw["overall"] || [],
      readability: raw["Readability"] || raw["readability"] || [],
      topicAuthority:
        raw["Topic Authority"] ||
        raw["TopicAuthority"] ||
        raw["topicAuthority"] ||
        [],
      contentValue:
        raw["Content Value"] ||
        raw["ContentValue"] ||
        raw["contentValue"] ||
        [],
      seo: raw["SEO"] || raw["Seo"] || raw["seo"] || [],
    };
  }, [feedback]);

  // Sync prop to state
  useEffect(() => {
    setInternalBrief(currentBrief);
  }, [currentBrief]);

  // ✅ Listen for live updates from TextBox
  useEffect(() => {
    function onBriefUpdate(e) {
      setInternalBrief(e.detail);
    }
    window.addEventListener("brief:update", onBriefUpdate);
    return () => window.removeEventListener("brief:update", onBriefUpdate);
  }, []);

  // fetch versions when mode is 'versions'
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (mode === "versions" && currentArticleId) {
        setLoadingVersions(true);
        try {
          const data = await listArticleVersions(currentArticleId);
          if (!ignore) setVersions(data);
        } catch (e) {
          console.error(e);
        } finally {
          if (!ignore) setLoadingVersions(false);
        }
      } else if (!ignore) {
        setVersions([]);
        setLoadingVersions(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [mode, currentArticleId]);

  // fetch comments when mode is 'comments'
  async function fetchComments() {
    if (!currentArticleId) {
      setComments([]);
      return;
    }
    setLoadingComments(true);
    try {
      const rows = await listComments(currentArticleId);
      setComments((rows || []).filter((c) => c.status !== "resolved"));
    } catch (e) {
      console.error("Failed to load comments:", e);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }

  async function ensureRepliesLoaded(commentId) {
    if (repliesByComment[commentId]) return;
    try {
      const rows = await listReplies(commentId);
      setRepliesByComment((prev) => ({ ...prev, [commentId]: rows }));
      setReplyCounts((prev) => ({ ...prev, [commentId]: rows.length }));
    } catch (e) {
      console.error("Failed to load replies", e);
    }
  }

  async function loadResolvedIfNeeded() {
    if (!currentArticleId) return;
    if (resolvedComments.length > 0) return;
    try {
      setLoadingResolved(true);
      const list = await listComments(currentArticleId, "resolved");
      setResolvedComments(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Failed to load resolved comments:", e);
    } finally {
      setLoadingResolved(false);
    }
  }

  useEffect(() => {
    if (mode === "comments") {
      fetchComments();
    }
  }, [mode, currentArticleId]);

  useEffect(() => {
    function onUpdated(e) {
      if (e.detail?.articleId && e.detail.articleId === currentArticleId) {
        fetchComments();
      }
    }
    window.addEventListener("comments:updated", onUpdated);
    return () => window.removeEventListener("comments:updated", onUpdated);
  }, [currentArticleId]);

  useEffect(() => {
    async function onRefresh(e) {
      const id = e?.detail?.article_id || currentArticleId;
      if (!id) return;
      setLoadingVersions(true);
      try {
        const data = await listArticleVersions(id);
        setVersions(data || []);
      } catch (err) {
        console.error("versions:refresh failed", err);
      } finally {
        setLoadingVersions(false);
      }
    }
    window.addEventListener("versions:refresh", onRefresh);
    return () => window.removeEventListener("versions:refresh", onRefresh);
  }, [currentArticleId]);

  return (
    <Wrapper>
      <TopBar>
        <label style={{ fontSize: 12, color: "#6b7280" }}>View:</label>
        <Select value={mode} onChange={(e) => setMode(e.target.value)}>
          {/* ✅ CONDITIONAL RENDER */}
          {organization?.ai_enabled && <option value="scores">Scores</option>}
          <option value="comments">Comments</option>
          <option value="versions">Versions</option>
          {organization?.ai_enabled && (
            <option value="brief">Content Brief</option>
          )}
          {organization?.ai_enabled && (
            <option value="markupai">Markup AI</option>
          )}
          <option value="assets">Assets</option>
        </Select>
      </TopBar>

      {/* ====== SCORES VIEW ====== */}
      {mode === "scores" && (
        <>
          <Title style={{ paddingLeft: "16px" }}>Scoring</Title>
          {categoriesMeta.map(({ key, label }) => {
            const score = feedback?.[key] ?? "-";
            const issues = Array.isArray(improvements[key])
              ? improvements[key]
              : [];

            return (
              <Section key={key}>
                <SectionHeader
                  onClick={() => toggle(key)}
                  aria-expanded={open[key]}
                >
                  <span>{label}</span>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Score>{score}</Score>
                    <Chevron open={open[key]}>›</Chevron>
                  </div>
                </SectionHeader>

                {open[key] && (
                  <SectionBody>
                    {issues.length === 0 ? (
                      <Empty>Analyze for suggestions.</Empty>
                    ) : (
                      issues.map((it, idx) => {
                        const sev = (it.severity || "").toLowerCase();
                        return (
                          <Item key={idx}>
                            <Row>
                              <Badge sev={sev}>
                                {(it.severity || "low").toUpperCase()}
                              </Badge>
                            </Row>
                            <Reason>{it.reason || "—"}</Reason>

                            <ShowBtn
                              onClick={() => {
                                const payload = {
                                  category: key,
                                  index: idx,
                                  severity: it.severity,
                                  reason: it.reason,
                                  exact_text: it.exact_text,
                                  suggested_replacement:
                                    it.suggested_replacement,
                                  location_hint: it.location_hint,
                                };
                                if (onShowIssue) {
                                  onShowIssue(payload);
                                }
                                window.dispatchEvent(
                                  new CustomEvent("issues:show", {
                                    detail: payload,
                                  })
                                );
                              }}
                              title="Highlight this in the editor"
                            >
                              Show
                            </ShowBtn>
                          </Item>
                        );
                      })
                    )}
                  </SectionBody>
                )}
              </Section>
            );
          })}
        </>
      )}

      {/* ====== MARKUP AI VIEW ====== */}
      {mode === "markupai" && (
        <>
          <Title style={{ paddingLeft: "16px" }}>Markup AI</Title>
          {!currentArticleId ? (
            <Empty>Open or save an article first.</Empty>
          ) : (
            <>
              <ShowBtn
                style={{
                  width: "100%",
                  marginBottom: 12,
                  backgroundColor: "#00876e",
                }}
                disabled={checkingMarkup}
                onClick={() => {
                  setCheckingMarkup(true);
                  setMarkupError(null);
                  window.dispatchEvent(new CustomEvent("markupai:check"));
                }}
              >
                {checkingMarkup ? "Checking…" : "Check Against Style Guide"}
              </ShowBtn>

              {markupError && (
                <Empty style={{ color: "#991b1b" }}>{markupError}</Empty>
              )}

              {markupAiChecksMeta.map(({ key, label }) => {
                const check = markupChecks[key];
                const issues = check?.issues || [];

                return (
                  <Section key={key}>
                    <SectionHeader
                      onClick={() => toggleMarkup(key)}
                      aria-expanded={openMarkup[key]}
                    >
                      <span>{label}</span>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 8 }}
                      >
                        <Score>{check ? issues.length : "–"}</Score>
                        <Chevron open={openMarkup[key]}>›</Chevron>
                      </div>
                    </SectionHeader>

                    {openMarkup[key] && (
                      <SectionBody>
                        {!hasCheckedMarkup ? (
                          <Empty>Run checks to see results.</Empty>
                        ) : check?.error ? (
                          <Empty style={{ color: "#991b1b" }}>{check.error}</Empty>
                        ) : issues.length === 0 ? (
                          <Empty>No issues found.</Empty>
                        ) : (
                          issues.map((it, idx) => {
                            const sev = (it.severity || "").toLowerCase();
                            return (
                              <Item key={idx}>
                                <Row>
                                  <Badge sev={sev}>
                                    {(it.severity || "low").toUpperCase()}
                                  </Badge>
                                </Row>
                                <Reason>{it.reason || "—"}</Reason>
                                <ShowBtn
                                  onClick={() => {
                                    const payload = {
                                      category: key,
                                      index: idx,
                                      severity: it.severity,
                                      reason: it.reason,
                                      exact_text: it.exact_text,
                                      suggested_replacement: it.suggested_replacement,
                                      location_hint: it.location_hint,
                                    };
                                    if (onShowIssue) {
                                      onShowIssue(payload);
                                    }
                                    window.dispatchEvent(
                                      new CustomEvent("issues:show", { detail: payload })
                                    );
                                  }}
                                  title="Highlight this in the editor"
                                >
                                  Show
                                </ShowBtn>
                              </Item>
                            );
                          })
                        )}
                      </SectionBody>
                    )}
                  </Section>
                );
              })}
            </>
          )}
        </>
      )}

      {/* ====== COMMENTS VIEW ====== */}
      {mode === "comments" && (
        <>
          <Title style={{ paddingLeft: "16px" }}>Comments</Title>
          <div style={{ marginBottom: 10 }}>
            <Empty>
              <i>
                Select text to add context-specific comments, or click Add
                Comment for a general note.
              </i>
            </Empty>
          </div>

          {canAddComments && (
            <div style={{ marginBottom: 10 }}>
              <AddCommentBtn
                onClick={() => {
                  if (!currentArticleId) {
                    alert("Open or save an article first.");
                    return;
                  }
                  window.dispatchEvent(new CustomEvent("comments:new"));
                }}
                title="Add a comment for the current selection in the editor"
              >
                Add Comment
              </AddCommentBtn>
            </div>
          )}

          {!currentArticleId ? (
            <Empty>No article open.</Empty>
          ) : loadingComments ? (
            <Empty>Loading…</Empty>
          ) : comments.length === 0 ? (
            <Empty>No active comments.</Empty>
          ) : (
            comments.map((c) => (
              <Item key={c.id}>
                {c.exact_text && (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    <strong>Selection:</strong>{" "}
                    <em>
                      {c.exact_text.length > 120
                        ? c.exact_text.slice(0, 120) + "…"
                        : c.exact_text}
                    </em>
                  </div>
                )}
                <div style={{ marginTop: 6 }}>{c.body}</div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  {c.exact_text && (
                    <ShowBtn
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent("comments:show", {
                            detail: {
                              exact_text: c.exact_text,
                              occurrence_index: c.occurrence_index || 0,
                            },
                          })
                        )
                      }
                    >
                      Show
                    </ShowBtn>
                  )}
                  {canResolveComments && (
                    <SmallBtn
                      onClick={async () => {
                        try {
                          await resolveComment(c.id);
                          window.dispatchEvent(
                            new CustomEvent("comments:clear")
                          );
                          window.dispatchEvent(
                            new CustomEvent("comments:updated", {
                              detail: { articleId: currentArticleId },
                            })
                          );
                        } catch (e) {
                          console.error("Failed to resolve comment:", e);
                          alert("Failed to resolve comment.");
                        }
                      }}
                      title="Mark this comment as resolved"
                    >
                      Resolve
                    </SmallBtn>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "flex-end",
                      marginTop: 6,
                    }}
                  >
                    {(userRole === "Admin" || userRole === "Editor") && (
                      <TrashBtn
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            !window.confirm(
                              "Delete this comment? This cannot be undone."
                            )
                          )
                            return;
                          try {
                            await deleteComment(c.id);
                            setComments((prev) =>
                              prev.filter((x) => x.id !== c.id)
                            );
                            window.dispatchEvent(
                              new CustomEvent("comments:clear")
                            );
                          } catch (err) {
                            console.error("Failed to delete comment:", err);
                            alert("Failed to delete comment.");
                          }
                        }}
                        title="Delete comment"
                      >
                        <span aria-hidden>🗑️</span>
                      </TrashBtn>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    marginTop: 8,
                    paddingLeft: 12,
                    borderLeft: "2px solid #e5e7eb",
                  }}
                >
                  {(repliesByComment[c.id] || []).map((r) => (
                    <div
                      key={r.id}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          background: "#f3f4f6",
                          borderRadius: 8,
                          padding: "6px 8px",
                          flex: 1,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginBottom: 4,
                          }}
                        >
                          <strong>{r.author_name || r.created_by}</strong> •{" "}
                          {new Date(r.created_at).toLocaleString()}
                        </div>
                        <div style={{ whiteSpace: "pre-wrap" }}>{r.body}</div>
                      </div>

                      {(userRole === "Admin" ||
                        userRole === "Editor" ||
                        r.created_by === currentUserId) && (
                        <button
                          onClick={async () => {
                            await deleteReply(r.id);
                            setRepliesByComment((prev) => ({
                              ...prev,
                              [c.id]: (prev[c.id] || []).filter(
                                (x) => x.id !== r.id
                              ),
                            }));
                          }}
                          title="Delete reply"
                          style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: "#ef4444",
                            fontWeight: 700,
                          }}
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  ))}

                  {/* Add Reply */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginTop: 6,
                    }}
                  >
                    <input
                      placeholder="Write a reply…"
                      value={replyDrafts[c.id] || ""}
                      onChange={(e) =>
                        setReplyDrafts((prev) => ({
                          ...prev,
                          [c.id]: e.target.value,
                        }))
                      }
                      style={{
                        width: "100%",
                        boxSizing: "border-box", // ensures padding doesn't push it out of bounds
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "8px 10px",
                      }}
                      onFocus={() => ensureRepliesLoaded(c.id)}
                    />
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <button
                        onClick={async () => {
                          const body = (replyDrafts[c.id] || "").trim();
                          if (!body) return;
                          await addReply({
                            comment_id: c.id,
                            body,
                          });
                          const updatedReplies = await listReplies(c.id);
                          setRepliesByComment((prev) => ({
                            ...prev,
                            [c.id]: updatedReplies,
                          }));
                          setReplyDrafts((prev) => ({
                            ...prev,
                            [c.id]: "",
                          }));
                        }}
                        style={{
                          border: "1px solid #10b981",
                          background: "#10b981",
                          color: "#fff",
                          borderRadius: 8,
                          padding: "6px 14px",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Reply
                      </button>
                    </div>
                  </div>
                </div>
              </Item>
            ))
          )}

          {/* Resolved Comments Section */}
          {currentArticleId && (
            <div
              style={{
                marginTop: 20,
                borderTop: "1px solid #e5e7eb",
                paddingTop: 10,
              }}
            >
              <button
                onClick={async () => {
                  const next = !showResolved;
                  setShowResolved(next);
                  if (next) await loadResolvedIfNeeded();
                }}
                style={{
                  width: "100%",
                  background: "#00876e",
                  color: "#fff",
                  border: 0,
                  borderRadius: 8,
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 700,
                }}
              >
                {showResolved ? "Hide Resolved Comments" : "Resolved Comments"}
              </button>

              {showResolved && (
                <div style={{ marginTop: 10 }}>
                  {loadingResolved ? (
                    <Empty>Loading…</Empty>
                  ) : resolvedComments.length === 0 ? (
                    <Empty>No resolved comments.</Empty>
                  ) : (
                    resolvedComments.map((c) => (
                      <Item key={c.id}>
                        {c.exact_text && (
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            <strong>Selection:</strong>{" "}
                            <em>
                              {c.exact_text?.length > 120
                                ? c.exact_text.slice(0, 120) + "…"
                                : c.exact_text || "—"}
                            </em>
                          </div>
                        )}

                        <div style={{ marginTop: 6 }}>{c.body}</div>

                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          {c.exact_text && (
                            <ShowBtn
                              onClick={() =>
                                window.dispatchEvent(
                                  new CustomEvent("comments:show", {
                                    detail: {
                                      exact_text: c.exact_text,
                                      occurrence_index: c.occurrence_index || 0,
                                    },
                                  })
                                )
                              }
                              title="Highlight this in the editor"
                            >
                              Show
                            </ShowBtn>
                          )}
                        </div>
                      </Item>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ====== VERSIONS VIEW ====== */}
      {mode === "versions" && (
        <>
          <Title style={{ paddingLeft: "16px" }}>Version History</Title>
          {!currentArticleId ? (
            <Empty>Open or save an article to view versions.</Empty>
          ) : loadingVersions ? (
            <Empty>Loading versions…</Empty>
          ) : versions.length === 0 ? (
            <Empty>No versions yet. Use “Save” to create one.</Empty>
          ) : (
            versions.map((v) => (
              <Item key={v.id}>
                <Row style={{ justifyContent: "space-between" }}>
                  <div>
                    <strong>v{v.version_no}</strong> &nbsp;·&nbsp;
                    {new Date(v.created_at).toLocaleString()}
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      {v.title || "Untitled"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <SmallBtn
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent("versions:clearChanges")
                        );
                        onOpenVersion && onOpenVersion(v.id);
                      }}
                      title="Open this version in the editor"
                    >
                      Open
                    </SmallBtn>
                    {v.version_no > 1 && (
                      <SmallBtn
                        onClick={() => {
                          window.dispatchEvent(
                            new CustomEvent("versions:showChanges", {
                              detail: {
                                version_id: v.id,
                                article_id: v.article_id || currentArticleId,
                                version_no: v.version_no,
                              },
                            })
                          );
                        }}
                        title="Highlight what changed vs the previous version"
                      >
                        Show Changes
                      </SmallBtn>
                    )}
                    {v.version_no !== 1 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: 8,
                        }}
                      >
                        <TrashBtn
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (
                              !window.confirm(
                                `Delete version v${v.version_no}? This cannot be undone.`
                              )
                            )
                              return;
                            try {
                              await deleteArticleVersion(v.id);
                              window.dispatchEvent(
                                new CustomEvent("versions:clearChanges")
                              );
                              const aid = v.article_id || currentArticleId;
                              window.dispatchEvent(
                                new CustomEvent("versions:refresh", {
                                  detail: { article_id: aid },
                                })
                              );
                            } catch (err) {
                              console.error(err);
                              alert("Failed to delete version.");
                            }
                          }}
                          title="Delete this version"
                        >
                          <span aria-hidden>🗑️</span>
                        </TrashBtn>
                      </div>
                    )}
                  </div>
                </Row>
              </Item>
            ))
          )}
        </>
      )}

      {/* ====== CONTENT BRIEF VIEW ====== */}
      {mode === "brief" && (
        <>
          <Title style={{ paddingLeft: "16px" }}>Content Brief</Title>
          {!internalBrief ? (
            <Empty>Click the Generate button to create a content brief.</Empty>
          ) : (
            <>
              {/* Working Title */}
              <Item>
                <Label>Working Title</Label>
                <div style={{ fontWeight: 600, marginTop: 4 }}>
                  {internalBrief.title || "Untitled"}
                </div>
              </Item>

              {/* Purpose */}
              {internalBrief.purpose && internalBrief.purpose.length > 0 && (
                <Item>
                  <Label>Purpose</Label>
                  <div style={{ marginTop: 4 }}>
                    {Array.isArray(internalBrief.purpose)
                      ? internalBrief.purpose.join(", ")
                      : internalBrief.purpose}
                  </div>
                </Item>
              )}

              {/* Audience & Level */}
              <Item>
                <Label>Audience</Label>
                <div style={{ marginTop: 4 }}>
                  <strong>Primary:</strong>{" "}
                  {internalBrief.primaryAudience || "—"}
                </div>
                {internalBrief.secondaryAudience && (
                  <div style={{ marginTop: 4 }}>
                    <strong>Secondary:</strong>{" "}
                    {internalBrief.secondaryAudience}
                  </div>
                )}
                <div style={{ marginTop: 4 }}>
                  <strong>Level:</strong>{" "}
                  {internalBrief.audienceKnowledgeLevel || "—"}
                </div>
              </Item>

              {/* Tone & Length */}
              <Item>
                <Label>Tone & Length</Label>
                <div style={{ marginTop: 4 }}>
                  <strong>Tone:</strong>{" "}
                  {Array.isArray(internalBrief.tone)
                    ? internalBrief.tone.join(", ")
                    : internalBrief.tone || "—"}
                </div>
                <div style={{ marginTop: 4 }}>
                  <strong>Word Count:</strong>{" "}
                  {internalBrief.articleLength || "—"}
                </div>
              </Item>

              {/* Keywords */}
              <Item>
                <Label>Keywords</Label>
                <div style={{ marginTop: 4 }}>
                  <strong>Primary:</strong>{" "}
                  {internalBrief.primaryKeywordsRaw || "—"}
                </div>
                {internalBrief.secondaryKeywordsRaw && (
                  <div style={{ marginTop: 4 }}>
                    <strong>Secondary:</strong>{" "}
                    {internalBrief.secondaryKeywordsRaw}
                  </div>
                )}
              </Item>

              {/* Reference URLs */}
              {internalBrief.referenceUrlsRaw && (
                <Item>
                  <Label>Reference URLs</Label>
                  <div
                    style={{
                      marginTop: 4,
                      wordBreak: "break-all",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {internalBrief.referenceUrlsRaw}
                  </div>
                </Item>
              )}

              {/* Key Messages */}
              <Item>
                <Label>Key Messages</Label>
                <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
                  {internalBrief.keyMessagesRaw || "—"}
                </div>
              </Item>

              {/* Constraints / Mistakes */}
              {internalBrief.commonMistakesRaw && (
                <Item>
                  <Label>Constraints / Rules</Label>
                  <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
                    {internalBrief.commonMistakesRaw}
                  </div>
                </Item>
              )}

              {/* Additional Notes */}
              {internalBrief.additionalNotes && (
                <Item>
                  <Label>Additional Notes</Label>
                  <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
                    {internalBrief.additionalNotes}
                  </div>
                </Item>
              )}

              <div style={{ marginTop: 16 }}>
                <button
                  onClick={() => {
                    // ✅ Dispatch event to tell TextBox to open modal
                    if (onEditBrief) onEditBrief(internalBrief);
                    window.dispatchEvent(
                      new CustomEvent("generation:edit-brief", {
                        detail: internalBrief,
                      })
                    );
                  }}
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "#00876e",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  Edit & Regenerate
                </button>
              </div>
            </>
          )}
        </>
      )}
      {/* ====== ASSETS VIEW ====== */}
      {mode === "assets" && (
        <>
          <Title style={{ paddingLeft: "16px" }}>Attached Assets</Title>
          {!internalBrief?.uploaded_assets ||
          internalBrief.uploaded_assets.length === 0 ? (
            <Empty>No assets attached to this article.</Empty>
          ) : (
            <div style={{ padding: "0 2px" }}>
              {internalBrief.uploaded_assets.map((asset, idx) => (
                <Item key={idx}>
                  <a
                    href={asset.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#2563eb",
                      textDecoration: "none",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      wordBreak: "break-all",
                      fontSize: "14px",
                    }}
                  >
                    📄 {asset.name}
                  </a>
                </Item>
              ))}
            </div>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default Sidebar2;
